import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useContaDigital } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { formatCurrency, formatShortDateWithoutTimezone, parseCurrencyToNumber } from '@ibk/shared-utils';
import { BillType, OperationCode } from '../../enums';
import { useDeleteAccountsPayable } from '../useDeleteAccountsPayable';
import { useFormatAccountsPayable } from '../useFormatAccountsPayable/useFormatAccountsPayable';
import { useGetAccountsPayable } from '../useGetAccountsPayable';
export function useAccountsPayable(canSeeBalance) {
    const navigate = useNavigate();
    const { t, currentLanguage } = useTranslation();
    const isLoadingRef = useRef(false);
    const PAGE_SIZE = 10;
    const INITIAL_PAGE = 1;
    const { toExtract, totalAmountAccountsPayable, setTotalAmountAccountsPayable } = useFormatAccountsPayable();
    const [accountsPayableExtract, setAccountsPayableExtract] = useState();
    const [selectedAccountPayable, setSelectedAccountPayable] = useState();
    const [showAlert, setShowAlert] = useState(undefined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { data: contaDigitalData, isLoading: balanceLoading } = useContaDigital({ enabled: !!canSeeBalance });
    const showBalance = useAppSelector(state => state.showBalance.showBalance);
    const [page, setPage] = useState(INITIAL_PAGE);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [totalPages, setTotalPages] = useState(0);
    const hasLoadedInitialData = useRef(false);
    const { accountsPayable, loading: accountsPayableLoading, error: accountsPayableError, getAccountsPayable, requestState, resetRequestState, } = useGetAccountsPayable();
    const { loading: accountsPayableLoadingDelete, error: accountsPayableErrorDelete, deleteAccountsPayable, successDeleteAccountsPayableMessage, } = useDeleteAccountsPayable();
    const { isFeatureFlagWhiteListed, getFeatureFlags, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const initialFilters = useRef((() => {
        return {
            operationCode: undefined,
            startAt: undefined,
            endAt: undefined,
            minAmount: undefined,
            maxAmount: undefined,
        };
    })());
    const [filters, setFilters] = useState(initialFilters.current);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const startAtEndAtFilters = useMemo(() => {
        if (filters?.startAt &&
            filters?.endAt &&
            filters?.startAt !== initialFilters.current.startAt &&
            filters?.endAt !== initialFilters.current.endAt) {
            return `${formatShortDateWithoutTimezone(filters?.startAt, currentLanguage)} ${t('to')} ${formatShortDateWithoutTimezone(filters?.endAt, currentLanguage)}`;
        }
    }, [filters?.startAt, filters?.endAt, t, currentLanguage]);
    const minAmountMaxAmountFilters = useMemo(() => {
        if (filters?.maxAmount && !filters.minAmount) {
            return `${t('until')} ${formatCurrency(filters?.maxAmount)}`;
        }
        if (filters.minAmount && !filters.maxAmount) {
            return `${t('from')} ${formatCurrency(filters?.minAmount)}`;
        }
        if (filters.minAmount && filters.maxAmount) {
            return `${formatCurrency(filters?.minAmount)} ${t('to')} ${formatCurrency(filters?.maxAmount)}`;
        }
    }, [filters?.minAmount, filters?.maxAmount, t]);
    const operationCodeFilter = useMemo(() => {
        if (filters?.operationCode) {
            return {
                [OperationCode.TAX]: t('accounts-payable.operation-type-tax'),
                [OperationCode.INVOICE]: t('accounts-payable.operation-type-invoice'),
                [OperationCode.PAYROLL]: t('accounts-payable.operation-type-payroll'),
                [OperationCode.EMPLOYEE_TERMINATION_FEES]: t('accounts-payable.operation-type-termination-fees'),
                [OperationCode.EMPLOYEE_13TH_SALARY]: t('accounts-payable.operation-type-13th-salary'),
                [OperationCode.EMPLOYEE_VACATION]: t('accounts-payable.operation-type-vacation'),
            }[filters?.operationCode];
        }
    }, [filters?.operationCode, t]);
    const hasFilters = useMemo(() => {
        return Boolean(startAtEndAtFilters || minAmountMaxAmountFilters || operationCodeFilter);
    }, [startAtEndAtFilters, minAmountMaxAmountFilters, operationCodeFilter]);
    const amountAccountsPayable = useMemo(() => {
        return formatCurrency(totalAmountAccountsPayable);
    }, [totalAmountAccountsPayable]);
    const loading = useMemo(() => accountsPayableLoading || accountsPayableLoadingDelete, [accountsPayableLoading, accountsPayableLoadingDelete]);
    const error = useMemo(() => accountsPayableError !== undefined || accountsPayableErrorDelete !== undefined, [accountsPayableError, accountsPayableErrorDelete]);
    const noResultError = useMemo(() => !loading && !hasFilters && (accountsPayable?.data?.length ?? 0) <= 0, [loading, hasFilters, accountsPayable]);
    const noResultForFiltersError = useMemo(() => hasFilters && (accountsPayable?.data?.length ?? 0) <= 0 && !loading, [hasFilters, accountsPayable, loading]);
    const balance = useMemo(() => {
        const amount = typeof contaDigitalData?.balance === 'object' && contaDigitalData?.balance !== null
            ? (contaDigitalData.balance.amount ?? 0)
            : 0;
        return formatCurrency(amount);
    }, [contaDigitalData]);
    const getBalanceValue = () => {
        if (!showBalance)
            return '*****';
        if (balanceLoading)
            return undefined;
        return balance;
    };
    const calculateTotalPages = (total) => {
        return Math.ceil(total / PAGE_SIZE);
    };
    useEffect(() => {
        if (successDeleteAccountsPayableMessage) {
            setShowAlert({
                message: successDeleteAccountsPayableMessage,
                appearance: 'success',
            });
            setTimeout(() => {
                window.location.reload();
            }, 500);
        }
    }, [successDeleteAccountsPayableMessage]);
    useEffect(() => {
        if (accountsPayableErrorDelete) {
            setShowAlert({
                message: accountsPayableErrorDelete,
                appearance: 'error',
            });
        }
    }, [accountsPayableErrorDelete]);
    const loadInitialData = useCallback(async () => {
        const featureFlagsData = await getFeatureFlags();
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAGAMENTOS_GROUP_PAYMENTS, undefined, featureFlagsData);
        const params = {
            page: INITIAL_PAGE,
            pageSize: PAGE_SIZE,
            groupPayments: isWhiteListed ? true : undefined,
        };
        const response = await getAccountsPayable(params);
        if (response) {
            setAccountsPayableExtract(toExtract(response.data, isWhiteListed));
            setTotalAmountAccountsPayable(response.totalAmount ?? 0);
            const totalPagesCalculated = calculateTotalPages(response.total);
            setTotalPages(totalPagesCalculated);
            setHasMore(INITIAL_PAGE < totalPagesCalculated);
        }
    }, [getFeatureFlags, isFeatureFlagWhiteListed, getAccountsPayable, setTotalAmountAccountsPayable, toExtract]);
    const retryLoadInitialData = useCallback(async () => {
        resetRequestState();
        setAccountsPayableExtract(undefined);
        setPage(INITIAL_PAGE);
        setHasMore(true);
        setTotalPages(0);
        setTotalAmountAccountsPayable(0);
        await loadInitialData();
    }, [loadInitialData, resetRequestState, setTotalAmountAccountsPayable]);
    useEffect(() => {
        if (!accountsPayableExtract && !hasLoadedInitialData.current) {
            hasLoadedInitialData.current = true;
            loadInitialData();
        }
        else if (accountsPayableExtract && accountsPayableExtract?.length >= 0) {
            return;
        }
    }, [accountsPayableExtract, loadInitialData]);
    const loadMore = async () => {
        const nextPage = page + 1;
        if (isLoadingRef.current || !hasMore || nextPage > totalPages) {
            return;
        }
        isLoadingRef.current = true;
        setLoadingMore(true);
        try {
            const featureFlagsData = await getFeatureFlags();
            const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAGAMENTOS_GROUP_PAYMENTS, undefined, featureFlagsData);
            const params = {
                page: nextPage,
                pageSize: PAGE_SIZE,
                groupPayments: isWhiteListed ? true : undefined,
            };
            const response = await getAccountsPayable(params);
            if (response) {
                setAccountsPayableExtract(toExtract([...response.data], isWhiteListed));
                setPage(nextPage);
                const hasMorePages = nextPage < totalPages;
                setHasMore(hasMorePages);
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            isLoadingRef.current = false;
            setLoadingMore(false);
        }
    };
    function refreshAccountsPayable() {
        if (hasMore) {
            loadMore();
        }
    }
    function reset() {
        setFilters({ ...initialFilters.current });
    }
    function filter(partial) {
        setFilters(previous => ({ ...previous, ...partial }));
    }
    const handlePay = (accountPayable) => {
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAGAMENTOS_GROUP_PAYMENTS, undefined, cache?.data);
        const isGroupedOperation = accountPayable.operationCode === OperationCode.PAYROLL ||
            accountPayable.operationCode === OperationCode.EMPLOYEE_13TH_SALARY;
        if (isWhiteListed && isGroupedOperation && accountPayable.groupingDetails) {
            navigate('/pagamentos/pix/transferir/funcionarios', {
                state: {
                    totalAmount: accountPayable.groupingDetails.amount,
                    operationCode: accountPayable.operationCode,
                    batchOfAccountsPayable: accountPayable.groupingDetails.child,
                },
            });
            return;
        }
        switch (accountPayable.type) {
            case BillType.PIX:
                navigate('/pagamentos/pix/transferir', {
                    state: { accountPayable: { ...accountPayable, amount: parseCurrencyToNumber(accountPayable.amount ?? '0') } },
                });
                break;
            case BillType.BARCODE:
                navigate('/pagamentos/pagar/boleto', { state: { accountPayable: accountPayable } });
                break;
            case BillType.ACCOUNT:
                break;
        }
    };
    const handleDelete = (accountPayable) => {
        setShowDeleteModal(true);
        setSelectedAccountPayable(accountPayable);
    };
    const confirmDeleteAccountPayable = () => {
        setShowDeleteModal(false);
        deleteAccountsPayable(selectedAccountPayable?.id ?? '');
    };
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedAccountPayable(undefined);
    };
    return {
        loading,
        error,
        requestState,
        noResultError,
        noResultForFiltersError,
        balance,
        balanceLoading,
        accountsPayable,
        accountsPayableExtract,
        operationCodeFilter,
        startAtEndAtFilters,
        minAmountMaxAmountFilters,
        initialFilters: initialFilters.current,
        filters,
        hasFilters,
        showDeleteModal,
        selectedAccountPayable,
        setShowDeleteModal,
        setSelectedAccountPayable,
        closeDeleteModal,
        confirmDeleteAccountPayable,
        handlePay,
        handleDelete,
        setFilters,
        showFiltersModal,
        setShowFiltersModal,
        reset,
        filter,
        refreshAccountsPayable,
        getBalanceValue,
        loadMore,
        showAlert,
        setShowAlert,
        loadingMore,
        hasMore,
        totalPages,
        amountAccountsPayable,
        currentPage: page,
        retryLoadInitialData,
    };
}
